import { type PanelRow } from '../../payload/panels'
import { usePeriod } from '../../payload/PeriodContext'
import { CHART_HEIGHT, chartTheme } from '../charts/chartTheme'
import { buildBarsOption, buildSparkOption } from '../charts/countsOptions'
import EChart from '../charts/EChart'
import {
  BarsH,
  Card,
  CardHead,
  DataTable,
  EmptyState,
  Grid,
  HelpMark,
  InlineBar,
  Note,
  SectionHead,
  Td,
  Tile,
  Tiles,
  Tr,
} from '../components'
import { biggest, listOf, n, num, plural, s } from '../format'
import { useBlank } from './useBlank'

// ONLY THE TWO BREAKDOWNS THAT SAY SOMETHING. Device was Desktop vs Mobile,
// which the operating system list already tells you and in more detail; and
// surface duplicated the website-vs-app chart further up the section.
const KEEP_DIMS = ['App version', 'Operating system']

const DIM_INFO: Record<string, string[]> = {
  'Operating system': [
    'As first seen for each visitor. This is also the closest thing we have to a desktop-versus-mobile split, at more useful granularity.',
  ],
  'App version': [
    'The last version we saw each visitor on, so this is what people are running now rather than how an upgrade spread.',
    '"Never opened the app" is a visitor who only ever used the website.',
  ],
}

const SPARKS = [
  { key: 'visitors', title: 'Visitors' },
  { key: 'sessions', title: 'Visits' },
  { key: 'pageviews', title: 'Pages viewed' },
]

/** How many visitors we get, and where from. Visitors and visits, never people. */
const Traffic = () => {
  const { one, panel, series, windowSuffix } = usePeriod()
  const blank = useBlank()
  const o = one('overview')
  const sources = panel('traffic_sources')
  const devices = panel('devices')

  const srcTop = sources.slice(0, 8)
  const srcMax = Math.max(1, ...srcTop.map(src => n(src.visitors)))
  const notSources = srcTop.filter(
    src =>
      src.source_kind === 'direct' ||
      src.source_kind === 'internal' ||
      src.source_kind === 'unknown'
  )

  const byDim = devices
    .filter(r => KEEP_DIMS.includes(s(r.dimension)))
    .reduce<Record<string, PanelRow[]>>((acc, r) => {
      const dim = s(r.dimension)
      ;(acc[dim] = acc[dim] ?? []).push(r)
      return acc
    }, {})

  const traffic = series('traffic_weekly', 'traffic_daily')
  const bucket = traffic.grain === 'day' ? 'day' : 'week'
  const best = biggest(traffic.rows, 'visitors')

  return (
    <section className="grid gap-4">
      <SectionHead
        title="Traffic"
        question="How many visitors do we get, and where from? Counted as visitors and visits, never as people — most are anonymous."
      />

      <Tiles>
        <Tile hero label={`Visits${windowSuffix}`} value={num(o.visits)} />
        <Tile label="Pages viewed" value={num(o.pageviews)} />
        <Tile label="Visitors" value={num(o.visitors)} />
        <Tile
          label={`Busiest ${bucket}`}
          value={num(best ? best.visitors : null)}
        />
      </Tiles>

      <h3 className="text-[0.9375rem] font-medium">Over time</h3>
      {traffic.rows.length ? (
        <Grid cols={3}>
          {SPARKS.map(spark => (
            <Card key={spark.key}>
              <CardHead title={spark.title} />
              <EChart
                height={CHART_HEIGHT.spark}
                option={buildSparkOption({
                  rows: traffic.rows,
                  xKey: traffic.xKey,
                  yKey: spark.key,
                  title: spark.title,
                  grain: traffic.grain,
                })}
              />
            </Card>
          ))}
        </Grid>
      ) : (
        <EmptyState text="No activity in this period." />
      )}

      <Card>
        <CardHead
          title={`Website vs mobile app, by ${bucket}`}
          info={[
            `Someone who used both in the same ${bucket} appears in both bars, so the two do not add up to that ${bucket}'s visitor total.`,
            `A gap marks a ${bucket} we have not imported activity for yet — that is different from a quiet one, which shows as zero.`,
          ]}
        />
        {traffic.rows.length ? (
          <EChart
            height={CHART_HEIGHT.bars}
            option={buildBarsOption({
              rows: traffic.rows,
              xKey: traffic.xKey,
              grain: traffic.grain,
              label: `Website and app visitors by ${bucket}`,
              series: [
                {
                  key: 'web_visitors',
                  label: 'Website',
                  color: chartTheme.series[0],
                },
                {
                  key: 'app_visitors',
                  label: 'Mobile app',
                  color: chartTheme.series[1],
                },
              ],
            })}
          />
        ) : (
          <EmptyState text="No activity in this period." />
        )}
      </Card>

      <Card>
        <CardHead
          title="Where visitors come from"
          info={[
            'The site that referred them, where one was recorded. There is no campaign tracking in place, so this is the whole picture of how people find us — referrer is the only channel signal we have.',
            'Counted as a cohort: these are visitors whose first visit falls in the selected period, so the conversion columns describe the people a source sent during it.',
          ]}
        />
        {srcTop.length ? (
          <DataTable
            label="Where visitors come from"
            head={[
              { label: 'Source' },
              { label: '' },
              { label: 'Visitors', num: true },
              { label: 'Signup', num: true },
              { label: 'Signed in', num: true },
            ]}
          >
            {srcTop.map(src => (
              <Tr key={s(src.source)}>
                <Td>
                  {s(src.source)}
                  <HelpMark text={s(src.caveat)} />
                </Td>
                <Td>
                  <InlineBar
                    value={n(src.visitors)}
                    max={srcMax}
                    hint={`${plural(n(src.visitors), 'visitor')} · ${s(src.source)}`}
                  />
                </Td>
                <Td num>{num(src.visitors)}</Td>
                <Td num>{num(src.reached_signup)}</Td>
                <Td num>{num(src.verified)}</Td>
              </Tr>
            ))}
          </DataTable>
        ) : (
          blank('traffic_sources', 'Nobody arrived in this period.')
        )}
        <Note
          flag
          parts={
            notSources.length > 0 && [
              <strong key="lead">
                {plural(notSources.length, 'row')} here are not really
                sources:{' '}
              </strong>,
              listOf(notSources.map(src => `"${s(src.source)}"`)),
              '. They are traffic with no referring site recorded, or people arriving from our own pages — not new interest. The bars are real, the attribution is not.',
            ]
          }
        />
        <Note
          flag
          parts={[
            <strong key="lead">
              One entry point is missing from this table entirely.{' '}
            </strong>,
            'Our playground site is named as the referrer by a large share of visits, but it carries no tracking of its own, so none of the traffic that starts there appears anywhere on this page. Adding tracking to it is the single biggest gap in what we can see.',
          ]}
        />
      </Card>

      <h3 className="text-[0.9375rem] font-medium">What visitors use it on</h3>
      {Object.keys(byDim).length ? (
        <Grid cols={2}>
          {Object.entries(byDim).map(([dim, rows]) => (
            <Card key={dim}>
              <CardHead title={dim} info={DIM_INFO[dim]} />
              <BarsH
                rows={rows.slice(0, 8)}
                labelKey="value"
                label={dim}
                labelWidth={155}
                series={[
                  {
                    key: 'visitors',
                    label: 'Visitors',
                    color: chartTheme.series[0],
                  },
                ]}
              />
            </Card>
          ))}
        </Grid>
      ) : (
        blank('devices', 'Nobody was active in this period.')
      )}
    </section>
  )
}

export default Traffic
